<template>
    <span
        ref="trigger"
        class="vsud-info-tooltip"
        :class="{ 'vsud-info-tooltip--text': hasLabel }"
        tabindex="0"
        :aria-label="hasLabel ? null : 'Más información'"
    >
        <slot>
            <i class="ri-information-2-line vsud-info-tooltip__icon" aria-hidden="true"></i>
        </slot>
    </span>
</template>

<script>
import Tooltip from "bootstrap/js/dist/tooltip";

/**
 * Ícono de ayuda con un tooltip que se abre al pasar el mouse o al llegar con el
 * teclado. Si recibe contenido, ese contenido abre el tooltip en vez del ícono.
 *
 * El tooltip se dibuja en el body (Bootstrap): no lo recortan las tablas ni los modales
 * con scroll.
 *
 * Uso:
 *   <VsudInfoTooltip text="Minutos que el vehículo queda bloqueado…" />
 *   <VsudInfoTooltip text="Usa el valor de la compañía.">hereda</VsudInfoTooltip>
 */
export default {
    name: "VsudInfoTooltip",
    props: {
        text: {
            type: String,
            required: true,
        },
        position: {
            type: String,
            default: "top",
            validator: (value) => ["top", "bottom", "left", "right"].includes(value),
        },
    },
    computed: {
        hasLabel() {
            return Boolean(this.$slots.default);
        },
    },
    watch: {
        text() {
            this.createTooltip();
        },
        position() {
            this.createTooltip();
        },
    },
    mounted() {
        this.createTooltip();
    },
    beforeUnmount() {
        this.disposeTooltip();
    },
    methods: {
        createTooltip() {
            this.disposeTooltip();
            this.tooltip = new Tooltip(this.$refs.trigger, {
                title: this.text,
                placement: this.position,
                trigger: "hover focus",
                customClass: "vsud-info-tooltip__box",
            });
        },
        disposeTooltip() {
            if (this.tooltip) this.tooltip.dispose();
            this.tooltip = null;
        },
    },
};
</script>

<style scoped>
.vsud-info-tooltip {
    display: inline-flex;
    align-items: center;
    color: var(--blue);
    cursor: help;
    line-height: 1;
    border-radius: 0.25rem;
}
.vsud-info-tooltip:focus {
    outline: none;
}
.vsud-info-tooltip:focus-visible {
    outline: 2px solid var(--blue);
    outline-offset: 2px;
}
.vsud-info-tooltip__icon {
    font-size: 0.95rem;
}
/* Con texto propio: se marca con un subrayado punteado, sin el ícono. */
.vsud-info-tooltip--text {
    color: inherit;
    text-decoration: underline dotted;
    text-underline-offset: 2px;
}
</style>

<style>
/* El tooltip vive en el body, fuera del alcance de los estilos scoped. El tema compila
   los tooltips de Bootstrap con valores fijos: se pisan las propiedades, no las variables. */
.tooltip.vsud-info-tooltip__box {
    font-size: 0.78rem;
}
.tooltip.vsud-info-tooltip__box.show {
    opacity: 1;
}
.vsud-info-tooltip__box .tooltip-inner {
    max-width: 260px;
    padding: 0.35rem 0.6rem;
    color: #fff;
    background-color: #333;
    border-radius: 0.4rem;
    text-align: left;
    line-height: 1.3;
}
/* La flecha, del mismo color que la caja en cada posición. */
.vsud-info-tooltip__box.bs-tooltip-top .tooltip-arrow::before,
.vsud-info-tooltip__box.bs-tooltip-auto[data-popper-placement^="top"] .tooltip-arrow::before {
    border-top-color: #333;
}
.vsud-info-tooltip__box.bs-tooltip-end .tooltip-arrow::before,
.vsud-info-tooltip__box.bs-tooltip-auto[data-popper-placement^="right"] .tooltip-arrow::before {
    border-right-color: #333;
}
.vsud-info-tooltip__box.bs-tooltip-bottom .tooltip-arrow::before,
.vsud-info-tooltip__box.bs-tooltip-auto[data-popper-placement^="bottom"] .tooltip-arrow::before {
    border-bottom-color: #333;
}
.vsud-info-tooltip__box.bs-tooltip-start .tooltip-arrow::before,
.vsud-info-tooltip__box.bs-tooltip-auto[data-popper-placement^="left"] .tooltip-arrow::before {
    border-left-color: #333;
}
</style>
